import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  TrendingUp, 
  Bot, 
  Store, 
  Shield, 
  ArrowRight, 
  Sparkles,
  BarChart3,
  Coins,
  Building2,
  Clock,
  ChevronRight,
  Menu,
  X
} from 'lucide-react';

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const features = [
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Stocks & Crypto",
      description: "Trade stocks, cryptocurrencies, and precious metals all in one platform.",
      color: "text-[#2962FF]"
    },
    {
      icon: <Bot className="w-6 h-6" />,
      title: "AI-Powered Trading",
      description: "Let our advanced AI analyze markets 24/7 and execute trades automatically.",
      color: "text-[#6366F1]"
    },
    {
      icon: <Store className="w-6 h-6" />,
      title: "Small Business Investing",
      description: "Support local mom & pop businesses and earn returns as they grow.",
      color: "text-[#00E676]"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Secure & Transparent",
      description: "Bank-level security with clear fee structures. No hidden charges.",
      color: "text-[#F7931A]"
    }
  ];

  const stats = [
    { value: "$5", label: "Minimum Investment" },
    { value: "Live", label: "Small Business Market" },
    { value: "10%", label: "Small Biz Investment Rate" },
    { value: "Multiple", label: "Investment Options" }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#00E676] rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-black" />
              </div>
              <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Sraivest
              </span>
            </Link>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-gray-400 hover:text-white transition-colors">Features</a>
              <a href="#how-it-works" className="text-gray-400 hover:text-white transition-colors">How it Works</a>
              <a href="#fees" className="text-gray-400 hover:text-white transition-colors">Fees</a>
            </div>

            <div className="hidden md:flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" className="text-gray-300 hover:text-white" data-testid="nav-login-btn">
                  Log in
                </Button>
              </Link>
              <Link to="/register">
                <Button className="btn-primary rounded-full px-6" data-testid="nav-signup-btn">
                  Get Started
                </Button>
              </Link>
            </div>
            <button
              type="button"
              className="md:hidden inline-flex h-11 w-11 items-center justify-center rounded-lg border border-white/20 bg-white/5 text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-[#00E676]"
              aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-navigation"
              onClick={() => setMobileMenuOpen((open) => !open)}
              data-testid="mobile-nav-toggle"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
          {mobileMenuOpen && (
            <div id="mobile-navigation" className="md:hidden border-t border-white/10 py-4">
              <div className="flex flex-col gap-2">
                <a href="#features" onClick={() => setMobileMenuOpen(false)} className="rounded-lg px-4 py-3 text-gray-200 hover:bg-white/10">Features</a>
                <a href="#how-it-works" onClick={() => setMobileMenuOpen(false)} className="rounded-lg px-4 py-3 text-gray-200 hover:bg-white/10">How It Works</a>
                <a href="#fees" onClick={() => setMobileMenuOpen(false)} className="rounded-lg px-4 py-3 text-gray-200 hover:bg-white/10">Fees</a>
                <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="rounded-lg px-4 py-3 text-gray-200 hover:bg-white/10">Log in</Link>
                <Link to="/register" onClick={() => setMobileMenuOpen(false)} className="mx-4 mt-2 rounded-full bg-[#00E676] px-5 py-3 text-center font-semibold text-black">Create Account</Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 md:px-8 lg:px-12 overflow-hidden">
        {/* Background */}
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1620555791739-438a95e7ff65?crop=entropy&cs=srgb&fm=jpg&w=1920')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-[#0A0A0A]/80 to-[#0A0A0A]" />
        
        <div className="relative max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#6366F1]/20 border border-[#6366F1]/30">
                <Sparkles className="w-4 h-4 text-[#6366F1]" />
                <span className="text-sm text-[#6366F1]">AI-Powered Investment Platform</span>
              </div>
              
              <h1 
                className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight animate-fade-in"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Invest in{' '}
                <span className="text-[#00E676]">Local Businesses</span>
              </h1>
              
              <p className="text-lg md:text-xl text-gray-400 max-w-xl animate-fade-in stagger-1">
                . Back local businesses by card, starting from just $5.
                Stocks, crypto and metals are view-only for now. Investing is risky and returns are not guaranteed.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 animate-fade-in stagger-2">
                <Link to="/register">
                  <Button 
                    size="lg" 
                    className="btn-primary rounded-full px-8 py-6 text-lg group"
                    data-testid="hero-get-started-btn"
                  >
                    Start Investing
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="btn-secondary rounded-full px-8 py-6 text-lg"
                    data-testid="hero-login-btn"
                  >
                    Sign In
                  </Button>
                </Link>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 pt-8 border-t border-white/10 animate-fade-in stagger-3">
                {stats.map((stat, i) => (
                  <div key={i}>
                    <div className="text-2xl font-bold text-[#00E676] number">{stat.value}</div>
                    <div className="text-sm text-gray-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5 hidden lg:block">
              <div className="relative">
                <div className="absolute -inset-4 bg-[#00E676]/20 blur-3xl rounded-full" />
                <img 
                  src="https://images.unsplash.com/photo-1647067517631-aa36cda58a17?crop=entropy&cs=srgb&fm=jpg&w=800"
                  alt="Stock market data"
                  className="relative rounded-2xl border border-white/10 shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl md:text-5xl font-bold mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Everything You Need to{' '}
              <span className="text-[#00E676]">Grow Wealth</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              From traditional markets to supporting local businesses, diversify your portfolio with ease.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => (
              <div 
                key={i}
                className="card-dashboard group hover:border-white/20 animate-fade-in"
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <div className={`w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center mb-4 ${feature.color} group-hover:scale-110 transition-transform`}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {feature.title}
                </h3>
                <p className="text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-24 px-4 md:px-8 lg:px-12 bg-[#121212]/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl md:text-5xl font-bold mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              How <span className="text-[#6366F1]">Sraivest</span> Works
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                icon: <Building2 className="w-8 h-8" />,
                title: "Create Account",
                description: "Sign up in minutes. Add your banking info and agree to our transparent fee structure."
              },
              {
                step: "02",
                icon: <Coins className="w-8 h-8" />,
                title: "Fund & Choose",
                description: "Deposit funds starting from $5. Pick stocks, crypto, metals, or small businesses."
              },
              {
                step: "03",
                icon: <Bot className="w-8 h-8" />,
                title: "Let AI Trade",
                description: "Enable AI trading. Our algorithms buy low, sell high — automatically, 24/7."
              }
            ].map((item, i) => (
              <div key={i} className="relative">
                <div className="card-dashboard text-center">
                  <div className="text-6xl font-bold text-white/5 absolute top-4 left-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {item.step}
                  </div>
                  <div className="w-16 h-16 rounded-2xl bg-[#6366F1]/20 flex items-center justify-center mx-auto mb-6 text-[#6366F1]">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {item.title}
                  </h3>
                  <p className="text-gray-400">{item.description}</p>
                </div>
                {i < 2 && (
                  <ChevronRight className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 w-8 h-8 text-white/20" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Small Business Section */}
      <section className="py-24 px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1753351052046-8c6818304a4f?crop=entropy&cs=srgb&fm=jpg&w=800"
                alt="Small business owners"
                className="rounded-2xl border border-white/10"
              />
            </div>
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00E676]/20 border border-[#00E676]/30">
                <Store className="w-4 h-4 text-[#00E676]" />
                <span className="text-sm text-[#00E676]">Small Business Marketplace</span>
              </div>
              
              <h2 
                className="text-4xl md:text-5xl font-bold"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Support <span className="text-[#00E676]">Mom & Pop</span> Businesses
              </h2>
              
              <p className="text-gray-400 text-lg">
                Invest in local businesses at just 10% of their investment goal. When they cash out, 
                you earn proportional returns. Sraivest takes a 20% fee from the business, 
                and 10% from your earnings — everyone wins.
              </p>

              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#00E676]/20 flex items-center justify-center shrink-0">
                    <BarChart3 className="w-5 h-5 text-[#00E676]" />
                  </div>
                  <div>
                    <h4 className="font-semibold">$1,000 Minimum Listing</h4>
                    <p className="text-gray-400 text-sm">Small businesses can list to raise capital starting from $1,000</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#00E676]/20 flex items-center justify-center shrink-0">
                    <Clock className="w-5 h-5 text-[#00E676]" />
                  </div>
                  <div>
                    <h4 className="font-semibold">Watch Them Grow</h4>
                    <p className="text-gray-400 text-sm">Track your investments and see returns as businesses succeed</p>
                  </div>
                </div>
              </div>

              <Link to="/register">
                <Button className="btn-primary rounded-full px-8 py-6 text-lg mt-4" data-testid="business-cta-btn">
                  Start Supporting Local
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Fee Structure */}
      <section id="fees" className="py-24 px-4 md:px-8 lg:px-12 bg-[#121212]/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl md:text-5xl font-bold mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Transparent <span className="text-[#F7931A]">Fee Structure</span>
            </h2>
            <p className="text-gray-400 text-lg">No hidden charges. Know exactly what you pay.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Account Processing Fees */}
            <div className="card-dashboard">
              <h3 className="text-xl font-semibold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Account Processing Fee
              </h3>
              <div className="space-y-3">
                {[
                  { range: "Under $100", fee: "2%" },
                  { range: "Under $1,000", fee: "7%" },
                  { range: "Under $10,000", fee: "10%" },
                  { range: "Under $50,000", fee: "15%" },
                  { range: "Under $100,000", fee: "20%" },
                  { range: "Under $350,000", fee: "25%" },
                  { range: "$1M+", fee: "40%" }
                ].map((tier, i) => (
                  <div key={i} className="flex justify-between py-2 border-b border-white/5 last:border-0">
                    <span className="text-gray-400">{tier.range}</span>
                    <span className="font-semibold text-[#00E676] number">{tier.fee}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Cashout Fees */}
            <div className="card-dashboard">
              <h3 className="text-xl font-semibold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Cashout Processing Fee
              </h3>
              <div className="space-y-3">
                {[
                  { range: "Under $1,000", fee: "7%" },
                  { range: "Under $10,000", fee: "10%" },
                  { range: "Under $50,000", fee: "20%" },
                  { range: "Under $100,000", fee: "25%" },
                  { range: "Under $1M", fee: "35%" },
                  { range: "Businesses", fee: "35% flat" }
                ].map((tier, i) => (
                  <div key={i} className="flex justify-between py-2 border-b border-white/5 last:border-0">
                    <span className="text-gray-400">{tier.range}</span>
                    <span className="font-semibold text-[#F7931A] number">{tier.fee}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 md:px-8 lg:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-4xl md:text-5xl font-bold mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Ready to <span className="text-[#00E676]">Start Investing?</span>
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Join investors backing local businesses using AI to grow their wealth. Start with as little as $5.
          </p>
          <Link to="/register">
            <Button 
              size="lg" 
              className="btn-primary rounded-full px-12 py-6 text-lg"
              data-testid="final-cta-btn"
            >
              Create Free Account
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 md:px-8 lg:px-12 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#00E676] rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-black" />
              </div>
              <span className="font-bold text-xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Sraivest Financial
              </span>
            </div>
            <p className="text-gray-500 text-sm">
              © 2026 Sraivest Financial. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
